// ═══ THE FINAL LEVEL OF A PARCEL-YEAR — one answer for the parcel card and every report ═══
// (docs/final-value-rule-check.md — Philip, 2026-10-08)
//
// ⛔ THE FINAL VALUE IS THE BOTTOM-MOST LEVEL THAT HAS VALUES. Proposed, Appeal One, Appeal Two … — whichever is
// deepest with a figure in it. An opened level that is still empty does not count (the level above stands until a
// figure arrives), and a decision does not choose the level. Before this, four places gave four answers: the card's
// tax section followed this rule, Per SF let an empty level win, Tax Savings always read Appeal One, and the reports
// let an accepted decision override depth.
//
// ⛔ A WITHDRAWN OR DENIED LEVEL IS SKIPPED — the appeal was pulled, or refused, so the level above stands (Denied:
// Philip 2026-10-26, the same rule as Withdrawn). The skipped level is the one carrying a 'withdrawn' or 'denied'
// decision; a year whose Appeal Status is Withdrawn or Denied with no such record skips its deepest appeal level.
// ⛔ PROPOSED IS NEVER SKIPPED (Philip, 2026-10-10). Only an APPEAL level can be withdrawn; Proposed is the
// assessor's value, and it stands when the appeal is pulled. A 'withdrawn' decision recorded against Proposed
// itself skipped it, so a Withdrawn year whose only level is Proposed had NO final level and Level read blank
// (Orlando Airport: 03-24-30-0000-10-106 2021 and 2022, 03-24-30-0000-10-164 2021).
//
// ⛔ THE YEAR'S APPEAL STATUS SAYS WHETHER THE ANSWER IS FINAL (parcel_year_data.appeal_status):
//   Appeal Open / Under Review / Value Pending → 'pending': the outcome is not known yet
//   Value Accepted / Withdrawn / Denied        → 'final'
//   no status (a rolled year)                  → 'final' if a level carries an accepted decision, else 'pending'
// Value Accepted with no appeal level opened is Proposed accepted: final level Proposed, so reduction 0, savings $0.
import { APPEAL_STATUS } from '../constants/statusCodes';
import { marketTotalOf } from './perSf';

const WORDS = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

// proposed → 0, appeal_one → 1 … appeal_ten → 10, appeal_11 → 11; anything unreadable → -1 (never wins).
export function levelOrdinal(key) {
  if (key === 'proposed') return 0;
  const m = /^appeal_(.+)$/.exec(String(key || ''));
  if (!m) return -1;
  const i = WORDS.indexOf(m[1]);
  if (i >= 0) return i + 1;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) && n > 0 ? n : -1;
}

const positive = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[$,\s]/g, ''));
  return Number.isFinite(n) && n > 0;
};

// A level has values when any of its columns has a taxable or market figure above 0.
export const levelHasValues = (rows) => (rows || []).some((r) => r && (positive(r.taxable_total) || positive(marketTotalOf(r))));

const PENDING_STATUSES = [APPEAL_STATUS.appeal_open, APPEAL_STATUS.under_review, APPEAL_STATUS.value_pending];
// The outcomes after which the level appealed from stands: the decision on the level, and the year's status.
const SKIPPED_DECISIONS = ['withdrawn', 'denied'];
const SKIPPED_STATUSES = [APPEAL_STATUS.withdrawn, APPEAL_STATUS.denied];

// levels: { [levelKey]: rows[] } · decisions: { [levelKey]: 'accepted' | 'withdrawn' | 'denied' } · status: the year's Appeal Status
// → { level: levelKey | null, outcome: 'final' | 'pending', skipped: [withdrawn / denied levelKeys] }
export function resolveFinalLevel({ levels, decisions, status }) {
  const keys = Object.keys(levels || {}).filter((k) => (levels[k] || []).length && levelOrdinal(k) >= 0)
    .sort((a, b) => levelOrdinal(b) - levelOrdinal(a));   // deepest first
  const dec = decisions || {};
  let skipped = keys.filter((k) => SKIPPED_DECISIONS.includes(dec[k]) && levelOrdinal(k) >= 1);
  if (SKIPPED_STATUSES.includes(status) && !skipped.length) {
    const deepestAppeal = keys.find((k) => levelOrdinal(k) >= 1);
    if (deepestAppeal) skipped = [deepestAppeal];
  }
  const level = keys.find((k) => !skipped.includes(k) && levelHasValues(levels[k])) || null;
  const outcome = PENDING_STATUSES.includes(status) ? 'pending'
    : (status === APPEAL_STATUS.value_accepted || SKIPPED_STATUSES.includes(status)) ? 'final'
    : (Object.values(dec).includes('accepted') ? 'final' : 'pending');
  return { level, outcome, skipped };
}
